/**
 * Scrolling-ancestor lookup, resolved from the **computed overflow** rather than from a hardcoded selector.
 *
 * Why computed overflow and not a selector: the app has four mutually exclusive shells
 * (`app.component.html` — operator / employee / office / logged-out), pages nest their own scroll boxes
 * (`layout-page-scrollable`'s `.page-scroller`, the composition editor's `#component-container`, the
 * automation-manager's column), and any of those can be the box that actually scrolls a given element.
 * A caller that hardcodes `#relative` or `.page-scroller` is wrong on some route, and — worse — is wrong
 * *silently*: it finds nothing, does nothing, and every structural check still passes.
 *
 * Extracted from `employees.component.ts` (#389, the frozen-header hand-over) so the ng-select
 * `appendTo` directive and the employees table share ONE implementation of "which box scrolls this?".
 */

/** The overflow values that make a box a scrollport. `hidden` is deliberately excluded — it clips but never scrolls. */
function scrolls(node: Element): boolean {
  const overflowY = getComputedStyle(node).overflowY;
  return overflowY === 'auto' || overflowY === 'scroll';
}

/**
 * The nearest ancestor that actually scrolls vertically — the app shell's content region in the real app,
 * an inner card list where a page nests one, the document elsewhere.
 *
 * Returns `document.scrollingElement` when no ancestor scrolls, so callers always get a coordinate origin.
 *
 * @param element the element whose scrollport is wanted (the element itself is NOT considered).
 */
export function scrollParentOf(element: HTMLElement): HTMLElement | Element | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (scrolls(node)) return node;
  }
  return document.scrollingElement;
}

/**
 * *Every* scrolling ancestor, nearest first — not just the closest one.
 *
 * The single-parent form is enough when you only need a coordinate origin, but it is NOT enough for keeping a
 * portaled dropdown attached to its input: in the composition editor the select sits inside
 * `#component-container` (`overflow-y-auto`) which itself sits inside the office shell's scroll region, and
 * scrolling *either* box moves the input. `scroll` events do not bubble from elements, so a listener on the
 * nearest box alone would miss the outer one entirely.
 *
 * The document/window is not included — it is not an `HTMLElement` and its scroll events are delivered on
 * `document`, so callers listen for it separately.
 *
 * @param element the element to walk up from (the element itself is NOT considered).
 */
export function scrollParentsOf(element: HTMLElement): HTMLElement[] {
  const parents: HTMLElement[] = [];
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (scrolls(node)) parents.push(node);
  }
  return parents;
}
